/**
 * One person's account.
 *
 * Three things happen here, and they are separated because they mean different
 * things: changing what somebody does, letting them back in after five wrong
 * passwords, and putting an account on hold when they leave.
 *
 * Putting somebody on hold rather than deleting them is deliberate — an
 * account that has taken money is part of the record, and removing it would
 * orphan every order it rang up. Archiving is the stronger step for somebody
 * who has actually left: the row stays (so nothing it touched breaks) but they
 * drop off the list and can no longer sign in, and it can be undone.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { formatServerTime, formatPKR } from '@foodrun/shared';
import {
  accountState,
  canChangeOwnRoles,
  clearable,
  suggestPassword,
  readableRole,
} from './staff.js';
import { RolePicker } from './RolePicker.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';

export function PersonSheet({
  person,
  roles,
  me,
  readOnly,
  canDeactivate,
  canArchive,
  canSettle,
  onClose,
  onChanged,
}) {
  const currentRoleIds = roles.filter((r) => person.roles?.includes(r.name)).map((r) => r.id);

  const [roleIds, setRoleIds] = useState(currentRoleIds);
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState(person.phone ?? '');
  const state = accountState(person);

  const chosenNames = roles.filter((r) => roleIds.includes(r.id)).map((r) => r.name);
  const ownRoles = canChangeOwnRoles(me, person, chosenNames);
  const rolesChanged =
    roleIds.length !== currentRoleIds.length || roleIds.some((id) => !currentRoleIds.includes(id));

  const saveRoles = useMutation({
    mutationFn: () => api.put(`/admin/staff/${person.id}/roles`, { roleIds }),
    onSuccess: onChanged,
  });

  const setPasswordFor = useMutation({
    mutationFn: () => api.post(`/admin/staff/${person.id}/password`, { password }),
  });

  const unlock = useMutation({
    mutationFn: () => api.post(`/admin/staff/${person.id}/unlock`),
    onSuccess: onChanged,
  });

  const hold = useMutation({
    mutationFn: (isActive) => api.patch(`/admin/staff/${person.id}`, { isActive }),
    onSuccess: onChanged,
  });

  const archive = useMutation({
    mutationFn: () => api.post(`/admin/staff/${person.id}/archive`),
    onSuccess: onChanged,
  });

  const restore = useMutation({
    mutationFn: () => api.post(`/admin/staff/${person.id}/restore`),
    onSuccess: onChanged,
  });

  const archived = Boolean(person.archived_at);
  // Somebody who only has a tab here: no login, no roles, no password to set.
  const accountOnly = Number(person.account_only) === 1;

  // What this person has on their account, and clearing it.
  const owes = Number(person.account_balance) || 0;
  const [clearing, setClearing] = useState(false);
  /**
   * How much is being cleared, and a word about it.
   *
   * A tab is often paid down a bit at a time, out of a week's wages, and "all
   * or nothing" left a manager unable to record what had actually been handed
   * over.
   *
   * The field starts EMPTY. It used to start filled in with everything owed,
   * and the first person to use it typed the part payment into the note
   * instead, pressed the button, and cleared the whole tab. An amount nobody
   * typed is not one anybody chose — so the whole amount is one press away,
   * and never the default.
   */
  const [clearAmount, setClearAmount] = useState('');
  const [clearNote, setClearNote] = useState('');
  const payment = clearable(clearAmount, owes);
  const settleAccount = useMutation({
    mutationFn: () =>
      api.post(`/admin/staff/${person.id}/settle-account`, {
        amount: payment.amount,
        ...(clearNote.trim() ? { note: clearNote.trim() } : {}),
      }),
    onSuccess: onChanged,
  });

  /**
   * A phone number can be added to somebody who is already here.
   *
   * It could be set when the account was made and never afterwards, which left
   * everybody hired before it mattered without one. It matters because the phone
   * app signs people in by phone number — the field says so and shows one as its
   * example — so a waiter with no number on their record has to be told to type
   * an email address into a box asking for a phone.
   */
  const savePhone = useMutation({
    mutationFn: () => api.patch(`/admin/staff/${person.id}`, { phone: phone.trim() }),
    onSuccess: onChanged,
  });

  const phoneChanged = phone.trim() !== (person.phone ?? '');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${person.first_name} ${person.last_name}`}
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <div className="w-full max-w-lg rounded-2xl bg-surface p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-semibold text-lg text-ink">
              {person.first_name} {person.last_name}
            </h2>
            <p className="truncate text-sm text-ink-2">{accountOnly ? 'Account holder · no login' : person.email}</p>
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${state.tone}`}>
            {state.label}
          </span>
        </div>

        <dl className="mt-4 space-y-1 text-sm">
          {!accountOnly && (
            <div className="flex justify-between gap-2">
              <dt className="text-ink-2">Doing</dt>
              <dd className="text-ink">{(person.roles ?? []).map(readableRole).join(', ') || '—'}</dd>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <dt className="text-ink-2">Phone</dt>
            <dd className="text-ink">{person.phone || 'None'}</dd>
          </div>
          {!accountOnly && (
            <div className="flex justify-between gap-2">
              <dt className="text-ink-2">Last signed in</dt>
              <dd className="text-ink">
                {person.last_login ? formatServerTime(person.last_login) : 'Never'}
              </dd>
            </div>
          )}
          {owes > 0 && (
            <div className="flex justify-between gap-2">
              <dt className="text-ink-2">On account</dt>
              <dd className="font-medium text-warn tabular-nums">{formatPKR(owes)}</dd>
            </div>
          )}
        </dl>

        {/* What they ate and when, and what has been paid — for the people who
            answer for the money. Offered whether or not anything is owed now: a
            cleared tab still has a history. */}
        {canSettle && (
          <Link
            to={`/staff/${person.id}/account`}
            className="mt-3 inline-block text-sm font-medium text-brand-300 hover:underline"
          >
            See account history →
          </Link>
        )}

        {/* What the staff member has put on their tab, and the one button that
            clears it — for a manager or the owner only. Shown whatever else the
            account is (active, on hold, even removed), because a debt is a debt. */}
        {owes > 0 && canSettle && (
          <div className="mt-4 rounded-lg bg-warn-bg px-3.5 py-3">
            {clearing ? (
              <>
                <p className="text-sm text-warn">
                  {person.first_name} owes {formatPKR(owes)}. Enter what has been paid &mdash; all
                  of it, or part.
                </p>
                <div className="mt-2.5 space-y-2.5">
                  <Field
                    label="Amount paid (Rs)"
                    value={clearAmount}
                    onChange={(e) => setClearAmount(e.target.value)}
                    inputMode="decimal"
                    placeholder="e.g. 500"
                    autoFocus
                    error={clearAmount.trim() && !payment.ok ? payment.reason : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setClearAmount(String(owes))}
                    className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-brand-300 hover:bg-brand-400/15"
                  >
                    Paid everything &mdash; {formatPKR(owes)}
                  </button>
                  <Field
                    label="Note (optional) — words, not the amount"
                    value={clearNote}
                    onChange={(e) => setClearNote(e.target.value)}
                    placeholder="From September salary"
                  />
                </div>
                {payment.ok && (
                  <p className="mt-2 text-sm text-warn">
                    {payment.left > 0
                      ? `${formatPKR(payment.left)} will still be owed.`
                      : 'This clears the account to nothing.'}
                  </p>
                )}
                {settleAccount.isError && (
                  <p role="alert" className="mt-2 text-sm text-bad">
                    {settleAccount.error?.message ?? 'That did not go through.'}
                  </p>
                )}
                <div className="mt-2.5 flex gap-2">
                  <Button
                    busy={settleAccount.isPending}
                    disabled={!payment.ok}
                    onClick={() => settleAccount.mutate()}
                  >
                    {payment.ok ? `Record ${formatPKR(payment.amount)} paid` : 'Record payment'}
                  </Button>
                  <Button variant="secondary" onClick={() => setClearing(false)}>
                    Go back
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-warn">
                  {person.first_name} has {formatPKR(owes)} on their account.
                </p>
                <Button variant="secondary" className="mt-2.5" onClick={() => setClearing(true)}>
                  Record a payment
                </Button>
              </>
            )}
          </div>
        )}

        {state.label === 'Locked out' && !readOnly && (
          <div className="mt-4 rounded-lg bg-warn-bg px-3.5 py-3">
            <p className="text-sm text-warn">
              Five wrong passwords. On a shared till that is usually the previous shift&rsquo;s
              password being tried.
            </p>
            <Button
              variant="secondary"
              className="mt-2.5"
              busy={unlock.isPending}
              onClick={() => unlock.mutate()}
            >
              Let them back in
            </Button>
          </div>
        )}

        {!readOnly && archived && (
          <div className="mt-5 border-t border-rule pt-4">
            <p className="text-sm text-ink-2">
              This account was removed from the list, so it cannot sign in. Its orders and shifts
              were kept &mdash; nothing was deleted. Restoring brings it back on hold; let them sign
              in again when you&rsquo;re ready.
            </p>
            {restore.error && (
              <p role="alert" className="mt-2 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
                {restore.error.message}
              </p>
            )}
            <Button
              className="mt-2.5 w-full"
              busy={restore.isPending}
              onClick={() => restore.mutate()}
            >
              Restore to the list
            </Button>
          </div>
        )}

        {!readOnly && !archived && (
          <>
            <div className="mt-5">
              <Field
                label="Phone"
                hint={accountOnly ? 'For calling them about the account.' : 'What they type to sign in on the phone app.'}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0300 1234567"
                inputMode="tel"
              />
              {phoneChanged && (
                <Button
                  variant="secondary"
                  className="mt-2"
                  busy={savePhone.isPending}
                  onClick={() => savePhone.mutate()}
                >
                  Save phone
                </Button>
              )}
              {savePhone.isError && (
                <p role="alert" className="mt-2 text-bad text-sm">
                  {savePhone.error?.message || 'Could not save the number.'}
                </p>
              )}
            </div>

            {!accountOnly && (
            <>
            <div className="mt-5">
              <RolePicker roles={roles} chosen={roleIds} onChange={setRoleIds} />

              {!ownRoles.ok && (
                <p role="alert" className="mt-2 text-sm text-bad">
                  {ownRoles.reason}.
                </p>
              )}

              {saveRoles.error && (
                <p
                  role="alert"
                  className="mt-2 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad"
                >
                  {saveRoles.error.message}
                </p>
              )}

              <Button
                className="mt-3 w-full"
                busy={saveRoles.isPending}
                disabled={!rolesChanged || roleIds.length === 0 || !ownRoles.ok}
                onClick={() => saveRoles.mutate()}
              >
                Save what they do
              </Button>
            </div>

            <div className="mt-5 border-t border-rule pt-4">
              <label htmlFor="reset" className="mb-1.5 block text-sm font-medium text-ink-2">
                Set a new password
              </label>
              <div className="flex gap-2">
                <input
                  id="reset"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank to keep the current one"
                  className="h-12 min-w-0 flex-1 rounded-lg border border-rule bg-surface-3 px-3.5 font-mono text-[15px] text-ink placeholder:font-sans placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setPassword(suggestPassword())}
                >
                  Suggest
                </Button>
              </div>

              {setPasswordFor.isSuccess && (
                <p className="mt-2 rounded-lg bg-good-bg px-3.5 py-2.5 text-sm text-good">
                  Done. They have been signed out everywhere and will need the new password.
                </p>
              )}
              {setPasswordFor.error && (
                <p
                  role="alert"
                  className="mt-2 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad"
                >
                  {setPasswordFor.error.message}
                </p>
              )}

              <Button
                variant="secondary"
                className="mt-2.5 w-full"
                busy={setPasswordFor.isPending}
                disabled={password.length < 8}
                onClick={() => setPasswordFor.mutate()}
              >
                Change it
              </Button>
            </div>

            <div className="mt-5 border-t border-rule pt-4">
              {person.is_active ? (
                <>
                  <p className="text-sm text-ink-2">
                    Putting an account on hold stops them signing in. Their orders and shifts stay
                    exactly as they are — an account that has taken money is part of the record.
                  </p>
                  {hold.error && (
                    <p
                      role="alert"
                      className="mt-2 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad"
                    >
                      {hold.error.message}
                    </p>
                  )}
                  <Button
                    variant="danger"
                    className="mt-2.5 w-full"
                    busy={hold.isPending}
                    disabled={!canDeactivate.ok}
                    title={canDeactivate.ok ? undefined : canDeactivate.reason}
                    onClick={() => hold.mutate(false)}
                  >
                    {canDeactivate.ok ? 'Put this account on hold' : canDeactivate.reason}
                  </Button>
                </>
              ) : (
                <Button
                  variant="secondary"
                  className="w-full"
                  busy={hold.isPending}
                  onClick={() => hold.mutate(true)}
                >
                  Let them sign in again
                </Button>
              )}
            </div>
            </>
            )}

            <div className="mt-5 border-t border-rule pt-4">
              <p className="text-sm text-ink-2">
                Removing takes them off the staff list and stops them signing in. Their orders and
                shifts are kept &mdash; nothing is deleted &mdash; and you can restore them later.
              </p>
              {archive.error && (
                <p
                  role="alert"
                  className="mt-2 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad"
                >
                  {archive.error.message}
                </p>
              )}
              <Button
                variant="danger"
                className="mt-2.5 w-full"
                busy={archive.isPending}
                disabled={!canArchive?.ok}
                title={canArchive?.ok ? undefined : canArchive?.reason}
                onClick={() => archive.mutate()}
              >
                {canArchive?.ok ? 'Remove from the list' : canArchive?.reason}
              </Button>
            </div>
          </>
        )}

        <Button variant="secondary" size="lg" className="mt-5 w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
